import { ChangeDetectionStrategy, Component, OnDestroy, ViewChild, inject, signal } from '@angular/core';
import { GalleryDto } from '@nusign/picflow-backend-types';
import { Subject, takeUntil } from 'rxjs';
import { DialogOptions } from '../../../interfaces/dialog-data.interface';
import { DialogService, OnGetData } from '../../../services/dialog.service';
import { GalleryService } from '../../../services/gallery.service';
import { ObservableDataLoaderService } from '../../../services/observable-data-loader.service';
import { TenantService } from '../../../services/tenant.service';
import { DialogModeSwitcherComponent } from '../../../shared-extra/components/dialog-mode-switcher/dialog-mode-switcher.component';
import {
  StickyFooterComponent,
  StickyFooterItem,
} from '../../../shared-extra/components/sticky-footer/sticky-footer.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { ResizeDirective } from '../../../shared/directives/resize.directive';
import { ShareGalleryCollaboratorsComponent } from '../../components/share-gallery-collaborators/share-gallery-collaborators.component';
import { ShareGalleryEmbedComponent } from '../../components/share-gallery-embed/share-gallery-embed.component';
import { ShareGalleryLinkComponent } from '../../components/share-gallery-link/share-gallery-link.component';
import { ShareSubGalleryDialogComponent } from '../share-sub-gallery-dialog/share-sub-gallery-dialog.component';
import { WorkspaceDialogComponent } from '../workspace-dialog/workspace-dialog.component';

@Component({
  selector: 'app-share-gallery-dialog',
  templateUrl: './share-gallery-dialog.component.html',
  styleUrls: ['./share-gallery-dialog.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ResizeDirective,
    DialogModeSwitcherComponent,
    ButtonDirective,
    SvgComponent,
    ShareGalleryCollaboratorsComponent,
    ShareGalleryLinkComponent,
    ShareGalleryEmbedComponent,
    StickyFooterComponent,
  ],
})
export class ShareGalleryDialogComponent implements OnGetData, OnDestroy {
  private galleryService = inject(GalleryService);
  private observableLoader = inject(ObservableDataLoaderService);
  private tenantService = inject(TenantService);
  private dialog = inject(DialogService);
  dialogName = 'share-gallery';
  close: (...args) => void;
  gallery = signal<GalleryDto>(undefined);
  mode = signal<'collaborators' | 'link' | 'embed'>('collaborators');
  inviteMode = signal<null | 'email' | 'choice'>(null);
  @ViewChild('collaborators') collaborators: ShareGalleryCollaboratorsComponent;
  hideAll = signal(false);
  destroyed$ = new Subject<void>();

  menuItems: StickyFooterItem[] = [
    {
      key: 'collaborators',
      icon: 'user',
      text: 'Collaborators',
    },
    {
      key: 'link',
      icon: 'link',
      text: 'Gallery Link',
    },
    {
      key: 'embed',
      icon: 'code-circle',
      text: 'Embed Code',
    },
  ];

  dialogOptions(): DialogOptions {
    return { size: 560, hideClose: true, spacingX: 40, spacingY: 40, minHeight: 415 };
  }

  onGetData(data: { gallery: GalleryDto; mode: 'collaborators' | 'link' | 'embed' }, close: (...args) => void) {
    if (this.tenantService.shouldShowWorkspaceDialog(data.gallery)) {
      this.hideAll.set(true);
      return this.dialog.open(WorkspaceDialogComponent, {
        onDone: () => {
          if (data.gallery.section) {
            this.dialog.open(ShareSubGalleryDialogComponent, data);
          } else {
            this.dialog.open(ShareGalleryDialogComponent, data);
          }
        },
        gallery: this.gallery(),
      });
    }
    this.close = close;
    this.gallery.set(data.gallery);
    if (data.mode) this.mode.set(data.mode);
    this.observableLoader.removeFuzzy('gallery_members');
    this.observableLoader.remove('members');

    if (this.gallery().section) {
      console.warn('sub-gallery opened in share dialog');
      return this.dialog.open(ShareSubGalleryDialogComponent, { gallery: this.gallery() });
    }

    this.galleryService
      .getGallery(this.gallery().id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(gallery => {
        this.gallery.set(gallery);
      });

    this.galleryService.onGalleryDeleted$.pipe(takeUntil(this.destroyed$)).subscribe(gallery => {
      if (!gallery || !this.gallery() || gallery.id !== this.gallery().id) return;
      this.close();
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
